// src/pages/WineryManagerPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Wine, Calendar, BookOpen, Plus, ChevronLeft, ChevronRight, Save, Trash2, Loader, ArrowLeft, Check, Search, Image as ImageIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getStoredToken } from "../utils/authStorage";

const API = process.env.REACT_APP_API_URL || "https://g6fw4rjkvb.execute-api.eu-central-1.amazonaws.com/prototype";
const req = async (path, opts={}) => {
  const token = getStoredToken("winery_owner");
  const r = await fetch(`${API}${path}`, {headers:{"Content-Type":"application/json",...(token?{Authorization:`Bearer ${token}`}:{})},...opts});
  const data = await r.json();
  if (!r.ok) throw new Error(data.error||"Request failed");
  return data;
};

const MONTHS=["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS_SHORT=["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const PRICE_TIERS=[{v:1,l:"Budget (€)"},{v:2,l:"Moderate (€€)"},{v:3,l:"Premium (€€€)"},{v:4,l:"Luxury (€€€€)"}];

const S = {
  page:{minHeight:"100vh",background:"#F2F5EC",color:"#2C3820",fontFamily:"'Inter',sans-serif",paddingBottom:40},
  topbar:{display:"flex",alignItems:"center",gap:12,padding:"14px 20px",background:"#E8EDE0",borderBottom:"1px solid #1A1A1A",position:"sticky",top:0,zIndex:40},
  card:{background:"#EDF1E4",border:"1px solid #252525",borderRadius:10,padding:20,marginBottom:16},
  label:{fontSize:10,fontWeight:600,color:"#7A8E68",textTransform:"uppercase",letterSpacing:"0.1em",display:"block",marginBottom:6},
  input:{width:"100%",background:"#E8EDE0",border:"1px solid #222",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#2C3820",fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"},
  btn:(c="#6B2737")=>({padding:"10px 18px",background:c,border:"none",borderRadius:8,color:"#DCE4EC",fontSize:13,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",gap:6}),
  btnOut:{padding:"8px 14px",background:"transparent",border:"1px solid #252525",borderRadius:7,color:"#5A6E48",fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:5},
  tab:(a)=>({padding:"8px 14px 10px",background:"transparent",border:"none",borderBottom:a?"2px solid #6B2737":"2px solid transparent",color:a?"#886070":"#6A7E58",cursor:"pointer",fontSize:12,fontWeight:500,display:"flex",alignItems:"center",gap:5,transition:"all 0.15s"}),
};

function AccountPrivacyPanel({onDeleted}) {
  const [exporting,setExporting]=useState(false);
  const [confirming,setConfirming]=useState(false);
  const [deleting,setDeleting]=useState(false);
  const [error,setError]=useState("");

  async function handleExport() {
    setExporting(true); setError("");
    try {
      const data = await req("/auth/me/export");
      const blob = new Blob([JSON.stringify(data,null,2)], {type:"application/json"});
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href=url; a.download="wine4collect-my-data.json";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch(err) { setError(err.message); }
    finally { setExporting(false); }
  }

  async function handleDelete() {
    setDeleting(true); setError("");
    try { await req("/auth/me", {method:"DELETE"}); onDeleted(); }
    catch(err) { setError(err.message); setDeleting(false); }
  }

  return (
    <div style={{...S.card, marginTop:16}}>
      <p style={{fontSize:13,fontWeight:600,color:"#2C3820",margin:"0 0 6px"}}>Your account data</p>
      <p style={{fontSize:12,color:"#6A7E58",margin:"0 0 12px"}}>
        Download a copy of your manager account, or permanently delete it (this also releases your winery back to unclaimed). See our{" "}
        <a href="/privacy" target="_blank" rel="noreferrer" style={{color:"#886070"}}>Privacy Policy</a>.
      </p>
      {error && <p style={{fontSize:12,color:"#8B1A2F",marginBottom:10}}>{error}</p>}
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <button onClick={handleExport} disabled={exporting} style={S.btnOut}>{exporting?<Loader size={13}/>:null} Export my data</button>
        {!confirming
          ? <button onClick={()=>setConfirming(true)} style={{...S.btnOut,color:"#8B1A2F",borderColor:"rgba(139,26,47,0.3)"}}>Delete my account</button>
          : null}
      </div>
      {confirming && (
        <div style={{marginTop:10,background:"rgba(139,26,47,0.08)",border:"1px solid rgba(139,26,47,0.2)",borderRadius:8,padding:12}}>
          <p style={{fontSize:12,color:"#8B1A2F",margin:"0 0 10px"}}>
            This permanently anonymizes your account and unclaims your winery. This can't be undone. Are you sure?
          </p>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setConfirming(false)} style={S.btnOut}>Cancel</button>
            <button onClick={handleDelete} disabled={deleting} style={S.btn("#8B1A2F")}>{deleting?<Loader size={13}/>:null} Yes, delete</button>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailsTab({winery,onSave}) {
  const [form,setForm]=useState({name:winery?.name||"",description:winery?.description||"",address:winery?.address||"",lat:winery?.lat||"",lng:winery?.lng||"",organic:winery?.organic||false,price_tier:winery?.price_tier||2,email:winery?.email||""});
  const [saving,setSaving]=useState(false);
  const [saved,setSaved]=useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const save=async()=>{
    setSaving(true);
    try{const u=await req(`/manage/winery/${winery.id}`,{method:"PUT",body:JSON.stringify(form)});onSave(u);setSaved(true);setTimeout(()=>setSaved(false),2000);}
    catch(e){alert("Save failed: "+e.message);}
    finally{setSaving(false);}
  };
  return (<div>
    <div style={S.card}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div style={{gridColumn:"1/-1"}}><label style={S.label}>Winery name</label><input style={S.input} value={form.name} onChange={e=>set("name",e.target.value)}/></div>
        <div style={{gridColumn:"1/-1"}}><label style={S.label}>Description</label><textarea style={{...S.input,minHeight:80,resize:"vertical"}} value={form.description} onChange={e=>set("description",e.target.value)}/></div>
        <div style={{gridColumn:"1/-1"}}><label style={S.label}>Address</label><input style={S.input} value={form.address} onChange={e=>set("address",e.target.value)}/></div>
        <div style={{gridColumn:"1/-1"}}><label style={S.label}>Manager email <span style={{fontSize:10,color:"#6A7E58"}}>(for booking notifications)</span></label><input style={S.input} type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="manager@winery.com"/></div>
        <div><label style={S.label}>Latitude</label><input style={S.input} type="number" step="0.0001" value={form.lat} onChange={e=>set("lat",e.target.value)}/></div>
        <div><label style={S.label}>Longitude</label><input style={S.input} type="number" step="0.0001" value={form.lng} onChange={e=>set("lng",e.target.value)}/></div>
        <div><label style={S.label}>Price tier</label><select style={S.input} value={form.price_tier} onChange={e=>set("price_tier",parseInt(e.target.value))}>{PRICE_TIERS.map(t=><option key={t.v} value={t.v}>{t.l}</option>)}</select></div>
        <div style={{display:"flex",alignItems:"center",gap:8,paddingTop:20}}><input type="checkbox" id="org" checked={form.organic} onChange={e=>set("organic",e.target.checked)}/><label htmlFor="org" style={{fontSize:13,color:"#4A5E38",cursor:"pointer"}}>Certified organic</label></div>
      </div>
    </div>
    <div style={{display:"flex",justifyContent:"flex-end"}}><button onClick={save} disabled={saving} style={S.btn()}>{saving?<Loader size={13}/>:saved?<Check size={13}/>:<Save size={13}/>}{saved?"Saved!":"Save changes"}</button></div>
    {winery&&<div style={{...S.card,marginTop:16}}>
      <div style={{fontSize:10,fontWeight:600,color:"#7A8E68",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:12}}>Stats</div>
      <div style={{display:"flex",gap:24}}>
        {[["Total bookings",winery.total_bookings||0],["Upcoming",winery.upcoming_bookings||0],["Slots",winery.total_slots||0],["Rating",winery.rating||"—"]].map(([l,v])=>(
          <div key={l}><div style={{fontSize:20,fontWeight:700,color:"#1E2C14"}}>{v}</div><div style={{fontSize:11,color:"#6A7E58"}}>{l}</div></div>
        ))}
      </div>
    </div>}
    {winery&&<WineryGalleryCard wineryId={winery.id}/>}
  </div>);
}

function PrefillPanel({wineryId,startOpen,onApproved}) {
  const [proposal,setProposal]=useState(null);
  const [slots,setSlots]=useState([]);
  const [open,setOpen]=useState(!!startOpen);
  const [saving,setSaving]=useState(false);
  const [err,setErr]=useState("");
  const [done,setDone]=useState(null);
  const [add,setAdd]=useState({date:"",time:"10:00"});

  useEffect(()=>{
    let alive=true;
    req(`/manage/winery/${wineryId}/prefill`).then(r=>{
      if(!alive)return;
      setProposal(r.proposal||null);
      setSlots((r.proposal?.slots||[]).map(x=>({...x})));
    }).catch(()=>{});
    return()=>{alive=false;};
  },[wineryId]);

  if(!proposal)return done?<div style={{...S.card,border:"1px solid #3D4A1A",color:"#3D4A1A",fontSize:13,marginBottom:16}}>✓ {done}</div>:null;
  const [py,pm]=proposal.month_start.split("-").map(Number);
  const monthLabel=`${MONTHS[pm-1]} ${py}`;
  const byDate={};
  slots.forEach(x=>{(byDate[x.date]||(byDate[x.date]=[])).push(x);});
  const dates=Object.keys(byDate).sort();
  const lastDay=new Date(py,pm,0).getDate();
  const minD=`${py}-${String(pm).padStart(2,"0")}-01`;
  const maxD=`${py}-${String(pm).padStart(2,"0")}-${String(lastDay).padStart(2,"0")}`;
  const same=(a,b)=>a.date===b.date&&a.slot_time===b.slot_time;

  const addSlot=()=>{
    if(!add.date||!add.time)return;
    if(slots.some(x=>x.date===add.date&&x.slot_time===add.time))return;
    const cap=slots[0]?.capacity||8;
    setSlots(ss=>[...ss,{date:add.date,slot_time:add.time,capacity:cap}].sort((a,b)=>(a.date+a.slot_time).localeCompare(b.date+b.slot_time)));
  };
  const approve=async()=>{
    setSaving(true);setErr("");
    try{
      const r=await req(`/manage/winery/${wineryId}/prefill/approve`,{method:"POST",body:JSON.stringify({proposal_id:proposal.id,slots})});
      setProposal(null);
      setDone(`Pre-fill saved: ${r.created} slots added for ${monthLabel}`+(r.skipped?.length?` (${r.skipped.length} skipped: closed or outside working hours)`:"")+".");
      onApproved&&onApproved();
    }catch(e){setErr(e.message);}finally{setSaving(false);}
  };

  return(<div style={{...S.card,border:"1px solid #6B2737",marginBottom:16}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
      <div>
        <div style={{fontSize:13,fontWeight:600,color:"#1E2C14"}}>Pre-filled booking slots for {monthLabel}</div>
        <div style={{fontSize:11,color:"#6A7E58",marginTop:2}}>{slots.length} slots on {dates.length} days, built from your working hours. Review, edit if needed, then approve.</div>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>setOpen(o=>!o)} style={S.btnOut}>{open?"Hide slots":"Revise"}</button>
        <button onClick={approve} disabled={saving||!slots.length} style={{...S.btn("#3D4A1A"),opacity:(saving||!slots.length)?0.6:1}}>{saving?<Loader size={13}/>:<Check size={13}/>}{open?"Save pre-fill":"Approve pre-fill"}</button>
      </div>
    </div>
    {err&&<div style={{marginTop:10,fontSize:12,color:"#8B1A2F"}}>{err}</div>}
    {open&&(<div style={{marginTop:14}}>
      <div style={{display:"flex",gap:8,alignItems:"flex-end",marginBottom:12,flexWrap:"wrap"}}>
        <div><label style={S.label}>Add slot — date</label><input style={{...S.input,width:"auto"}} type="date" min={minD} max={maxD} value={add.date} onChange={e=>setAdd(a=>({...a,date:e.target.value}))}/></div>
        <div><label style={S.label}>Time</label><input style={{...S.input,width:"auto"}} type="time" value={add.time} onChange={e=>setAdd(a=>({...a,time:e.target.value}))}/></div>
        <button onClick={addSlot} style={S.btn()}><Plus size={13}/>Add</button>
      </div>
      <div style={{maxHeight:360,overflowY:"auto"}}>
        {dates.map(d=>(<div key={d} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:"1px solid #1A1A1A",flexWrap:"wrap"}}>
          <span style={{fontSize:12,fontWeight:600,color:"#4A5E38",width:110}}>{d} <span style={{color:"#7A8E68",fontWeight:400}}>{DAYS_SHORT[(new Date(d+"T12:00:00").getDay()+6)%7]}</span></span>
          {byDate[d].map(x=>(<span key={x.slot_time} style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:11,background:"#E2D8DA",color:"#886070",padding:"3px 6px 3px 8px",borderRadius:100}}>
            {x.slot_time}
            <button title="Remove slot" onClick={()=>setSlots(ss=>ss.filter(y=>!same(x,y)))} style={{background:"none",border:"none",cursor:"pointer",color:"#886070",padding:0,fontSize:13,lineHeight:1}}>×</button>
          </span>))}
          <button title="Remove whole day" onClick={()=>setSlots(ss=>ss.filter(y=>y.date!==d))} style={{...S.btnOut,marginLeft:"auto",padding:"3px 8px",fontSize:11}}><Trash2 size={11}/>Day</button>
        </div>))}
        {!dates.length&&<div style={{fontSize:12,color:"#7A8E68",padding:8}}>No slots left in this pre-fill.</div>}
      </div>
    </div>)}
  </div>);
}

function AvailabilityTab({wineryId,prefillId,initialMonth}) {
  const today=new Date();
  const im=/^(\d{4})-(\d{2})$/.exec(initialMonth||"");
  const [year,setYear]=useState(im?+im[1]:today.getFullYear());
  const [month,setMonth]=useState(im?+im[2]:today.getMonth()+1);
  const [calData,setCalData]=useState(null);
  const [loading,setLoading]=useState(false);
  const [bf,setBf]=useState({open_time:"10:00",close_time:"17:00",slot_interval_min:90,capacity:8,days_ahead:90,open_days:[1,2,3,4,5,6]});
  const [saving,setSaving]=useState(false);
  const [selectedDate,setSelectedDate]=useState(null);

  const load=useCallback(async()=>{
    setLoading(true);
    try{setCalData(await req(`/manage/winery/${wineryId}/availability?year=${year}&month=${month}`));}
    catch(e){console.error(e);}finally{setLoading(false);}
  },[wineryId,year,month]);

  useEffect(()=>{load();},[load]);

  const changeMonth=d=>{let m=month+d,y=year;if(m>12){m=1;y++;}if(m<1){m=12;y--;}setMonth(m);setYear(y);setSelectedDate(null);};
  const toggleDay=d=>setBf(f=>({...f,open_days:f.open_days.includes(d)?f.open_days.filter(x=>x!==d):[...f.open_days,d].sort()}));

  const bulkAdd=async()=>{
    setSaving(true);
    try{const r=await req("/manage/availability/bulk",{method:"POST",body:JSON.stringify({...bf,winery_id:wineryId})});alert(`Added ${r.added} slots`);load();}
    catch(e){alert("Failed: "+e.message);}finally{setSaving(false);}
  };
  const deleteDay=async d=>{
    if(!window.confirm(`Delete all slots for ${d}?`))return;
    await req("/manage/availability",{method:"DELETE",body:JSON.stringify({winery_id:wineryId,date:d})});
    setSelectedDate(null);load();
  };

  const firstDay=new Date(year,month-1,1);
  const lastDay=new Date(year,month,0);
  const startOffset=(firstDay.getDay()+6)%7;
  const cells=[];
  for(let i=0;i<startOffset;i++)cells.push(null);
  for(let d=1;d<=lastDay.getDate();d++)cells.push(d);
  const weeks=[];
  for(let i=0;i<cells.length;i+=7)weeks.push(cells.slice(i,i+7));
  while(weeks[weeks.length-1]?.length<7)weeks[weeks.length-1].push(null);
  const ds=d=>`${year}-${String(month).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  const days=calData?.days||{};

  return (<><PrefillPanel wineryId={wineryId} startOpen={!!prefillId} onApproved={load}/><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
    <div style={S.card}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
        <button onClick={()=>changeMonth(-1)} style={S.btnOut}><ChevronLeft size={13}/></button>
        <span style={{fontSize:14,fontWeight:600,color:"#1E2C14"}}>{MONTHS[month-1]} {year}</span>
        <button onClick={()=>changeMonth(1)} style={S.btnOut}><ChevronRight size={13}/></button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:4}}>
        {DAYS_SHORT.map(d=><div key={d} style={{textAlign:"center",fontSize:9,color:"#7A8E68",fontWeight:600}}>{d}</div>)}
      </div>
      {weeks.map((week,wi)=>(
        <div key={wi} style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:3}}>
          {week.map((d,di)=>{
            if(!d)return <div key={di}/>;
            const dateStr=ds(d);
            const slots=days[dateStr]||[];
            const sel=selectedDate===dateStr;
            return(<button key={di} onClick={()=>setSelectedDate(sel?null:dateStr)} style={{
              aspectRatio:"1",borderRadius:6,border:sel?"1px solid #6B2737":"1px solid transparent",
              background:sel?"#E2D8DA":slots.length?"rgba(107,39,55,0.1)":"transparent",
              color:slots.length?"#886070":"#7A8E68",cursor:slots.length?"pointer":"default",
              fontSize:11,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:1,
            }}>{d}{slots.length>0&&<div style={{fontSize:7,color:"#6B2737"}}>{slots.length}s</div>}</button>);
          })}
        </div>
      ))}
      {loading&&<div style={{textAlign:"center",fontSize:12,color:"#7A8E68",padding:10}}>Loading…</div>}
    </div>

    <div>
      {selectedDate&&days[selectedDate]&&(
        <div style={{...S.card,marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <span style={{fontSize:13,fontWeight:600,color:"#1E2C14"}}>{selectedDate}</span>
            <button onClick={()=>deleteDay(selectedDate)} style={{...S.btnOut,color:"#886070",borderColor:"#D4A8B0"}}><Trash2 size={12}/>Delete day</button>
          </div>
          {days[selectedDate].map((slot,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #1A1A1A"}}>
              <span style={{fontSize:12,color:"#4A5E38"}}>{slot.slot_time}</span>
              <span style={{fontSize:12,color:slot.booked>0?"#886070":"#6A7E58"}}>{slot.booked}/{slot.capacity} booked</span>
            </div>
          ))}
        </div>
      )}
      <div style={S.card}>
        <div style={{fontSize:10,fontWeight:600,color:"#7A8E68",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:14}}>Bulk add slots</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div><label style={S.label}>Open time</label><input style={S.input} type="time" value={bf.open_time} onChange={e=>setBf(f=>({...f,open_time:e.target.value}))}/></div>
          <div><label style={S.label}>Close time</label><input style={S.input} type="time" value={bf.close_time} onChange={e=>setBf(f=>({...f,close_time:e.target.value}))}/></div>
          <div><label style={S.label}>Slot interval (min)</label><input style={S.input} type="number" min={30} step={15} value={bf.slot_interval_min} onChange={e=>setBf(f=>({...f,slot_interval_min:parseInt(e.target.value)}))}/></div>
          <div><label style={S.label}>Capacity/slot</label><input style={S.input} type="number" min={1} value={bf.capacity} onChange={e=>setBf(f=>({...f,capacity:parseInt(e.target.value)}))}/></div>
          <div style={{gridColumn:"1/-1"}}><label style={S.label}>Days ahead</label><input style={S.input} type="number" min={7} max={365} value={bf.days_ahead} onChange={e=>setBf(f=>({...f,days_ahead:parseInt(e.target.value)}))}/></div>
        </div>
        <div style={{marginBottom:12}}>
          <label style={S.label}>Open days</label>
          <div style={{display:"flex",gap:6}}>
            {DAYS_SHORT.map((d,i)=>{const dow=i+1;const a=bf.open_days.includes(dow);return(
              <button key={d} onClick={()=>toggleDay(dow)} style={{padding:"5px 8px",borderRadius:6,border:a?"1px solid #6B2737":"1px solid #222",background:a?"#E2D8DA":"transparent",color:a?"#886070":"#6A7E58",fontSize:11,cursor:"pointer"}}>{d}</button>
            );})}
          </div>
        </div>
        <button onClick={bulkAdd} disabled={saving} style={S.btn()}>{saving?<Loader size={13}/>:<Plus size={13}/>}Add slots</button>
      </div>
    </div>
  </div></>);
}

function BookingsTab({wineryId}) {
  const [bookings,setBookings]=useState([]);
  const [loading,setLoading]=useState(true);
  const [upcoming,setUpcoming]=useState(true);
  const [selected,setSelected]=useState(new Set());
  const [cancelTarget,setCancelTarget]=useState(null);
  const [actionLoading,setActionLoading]=useState(false);
  const statusColor={approved:"#3D4A1A",confirmed:"#3D4A1A",pending:"#886070",cancelled:"#7A8E68",rejected:"#8B1A2F"};

  const reload=()=>{setLoading(true);req(`/manage/winery/${wineryId}/bookings?upcoming=${upcoming}`).then(r=>{setBookings(r);setSelected(new Set());}).catch(console.error).finally(()=>setLoading(false));};
  useEffect(()=>{reload();},[wineryId,upcoming]);

  const allSelectable=bookings.filter(b=>b.status!=="cancelled"&&b.status!=="rejected");
  const allSelected=allSelectable.length>0&&allSelectable.every(b=>selected.has(b.id));
  const toggleAll=()=>{
    if(allSelected) setSelected(new Set());
    else setSelected(new Set(allSelectable.map(b=>b.id)));
  };

  const doAction=async(url,method="POST",body=null)=>{
    setActionLoading(true);
    try{await req(url,{method,...(body?{body:JSON.stringify(body)}:{})});}
    catch(e){alert(e.message);}
    finally{setActionLoading(false);reload();}
  };

  const doCancel=async(reason)=>{
    setActionLoading(true);
    const ids=cancelTarget==="bulk"?[...selected]:[cancelTarget];
    for(const id of ids){try{await req(`/booking/${id}`,{method:"DELETE",body:JSON.stringify({reason})});}catch(e){console.error(e);}}
    setCancelTarget(null);setActionLoading(false);reload();
  };

  const allPending=selected.size>0&&[...selected].every(id=>bookings.find(b=>b.id===id)?.status==="pending");
  const hasSelected=selected.size>0;

  return(<div style={S.card}>
    {cancelTarget&&(
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
        <div style={{background:"#F2F5EC",borderRadius:12,padding:24,maxWidth:380,width:"100%"}}>
          <div style={{fontSize:14,fontWeight:600,color:"#1E2C14",marginBottom:8}}>
            Cancel {cancelTarget==="bulk"?`${selected.size} bookings`:"booking"}
          </div>
          <p style={{fontSize:12,color:"#5A6E48",marginBottom:10}}>Reason will be sent to guest(s) by email.</p>
          <textarea id="cancelReason" placeholder="e.g. Winery closed for private event"
            style={{width:"100%",minHeight:70,padding:"8px 10px",borderRadius:8,border:"1px solid #C4D0A8",background:"#EDF1E4",color:"#2C3820",fontSize:12,resize:"vertical",boxSizing:"border-box"}}/>
          <div style={{display:"flex",gap:8,marginTop:10}}>
            <button onClick={()=>setCancelTarget(null)} style={{flex:1,padding:"8px",borderRadius:8,border:"1px solid #C4D0A8",background:"#EDF1E4",color:"#5A6E48",cursor:"pointer",fontSize:12}}>Back</button>
            <button onClick={()=>{const r=document.getElementById("cancelReason").value;if(r.trim())doCancel(r.trim());}}
              style={{flex:1,padding:"8px",borderRadius:8,border:"none",background:"#8B1A2F",color:"white",cursor:"pointer",fontSize:12,fontWeight:600}}>Confirm</button>
          </div>
        </div>
      </div>
    )}

    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
      <span style={{fontSize:14,fontWeight:600,color:"#1E2C14"}}>Bookings</span>
      <div style={{display:"flex",gap:6,alignItems:"center"}}>
        {hasSelected&&allPending&&<button onClick={()=>{for(const id of selected)doAction(`/booking/${id}/approve`);}} disabled={actionLoading}
          style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:"none",background:"#3D4A1A",color:"white",cursor:"pointer"}}>✓ Approve all</button>}
        {hasSelected&&allPending&&<button onClick={()=>{for(const id of selected)doAction(`/booking/${id}/reject`);}} disabled={actionLoading}
          style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:"none",background:"#8B1A2F",color:"white",cursor:"pointer"}}>✗ Reject all</button>}
        {hasSelected&&<button onClick={()=>setCancelTarget("bulk")} disabled={actionLoading}
          style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:"1px solid #8B1A2F",background:"transparent",color:"#8B1A2F",cursor:"pointer"}}>Cancel {selected.size}</button>}
        <button onClick={()=>setUpcoming(true)} style={S.tab(upcoming)}>Upcoming</button>
        <button onClick={()=>setUpcoming(false)} style={S.tab(!upcoming)}>All</button>
      </div>
    </div>

    {loading?<div style={{textAlign:"center",padding:30,color:"#6A7E58"}}>Loading…</div>:
    bookings.length===0?<div style={{textAlign:"center",padding:30,color:"#6A7E58",fontSize:13}}>No bookings yet</div>:
    <table style={{width:"100%",borderCollapse:"collapse"}}>
      <thead><tr style={{borderBottom:"1px solid #C4D0A8"}}>
        <th style={{paddingBottom:8,textAlign:"left"}}>
          <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{accentColor:"#8B1A2F"}}/>
        </th>
        {["Date","Time","Party","Guest","Status","Actions"].map((h,i)=>(
          <th key={i} style={{textAlign:"left",fontSize:10,color:"#7A8E68",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.1em",paddingBottom:8}}>{h}</th>
        ))}
      </tr></thead>
      <tbody>{bookings.map((b,i)=>{
        const canSelect=b.status!=="cancelled"&&b.status!=="rejected";
        const isPending=b.status==="pending";
        return(
          <tr key={i} style={{borderBottom:"1px solid #E8EDE0"}}>
            <td style={{padding:"8px 0"}}>
              {canSelect&&<input type="checkbox" checked={selected.has(b.id)}
                onChange={e=>{const s=new Set(selected);e.target.checked?s.add(b.id):s.delete(b.id);setSelected(s);}}
                style={{accentColor:"#8B1A2F"}}/>}
            </td>
            <td style={{padding:"8px 4px",fontSize:12,color:"#2C3820"}}>{b.visit_date}</td>
            <td style={{padding:"8px 4px",fontSize:12,color:"#4A5E38"}}>{b.slot_time}</td>
            <td style={{padding:"8px 4px",fontSize:12,color:"#4A5E38"}}>{b.party_size} pax</td>
            <td style={{padding:"8px 4px",fontSize:12,color:"#5A6E48"}}>{b.guest_email||"—"}</td>
            <td style={{padding:"8px 4px"}}>
              <span style={{fontSize:10,padding:"2px 8px",borderRadius:100,background:`${statusColor[b.status]||"#888"}22`,color:statusColor[b.status]||"#888"}}>{b.status}</span>
            </td>
            <td style={{padding:"8px 0"}}>
              <div style={{display:"flex",gap:4}}>
                {isPending&&<button onClick={()=>doAction(`/booking/${b.id}/approve`)} disabled={actionLoading} title="Approve"
                  style={{width:26,height:26,borderRadius:6,border:"none",background:"#3D4A1A",color:"white",cursor:"pointer",fontSize:13}}>✓</button>}
                {isPending&&<button onClick={()=>doAction(`/booking/${b.id}/reject`)} disabled={actionLoading} title="Reject"
                  style={{width:26,height:26,borderRadius:6,border:"none",background:"#8B1A2F",color:"white",cursor:"pointer",fontSize:13}}>✗</button>}
                {canSelect&&<button onClick={()=>setCancelTarget(b.id)} disabled={actionLoading} title="Cancel"
                  style={{width:26,height:26,borderRadius:6,border:"1px solid #C4D0A8",background:"transparent",color:"#8B1A2F",cursor:"pointer",fontSize:13}}>⊗</button>}
              </div>
            </td>
          </tr>
        );
      })}</tbody>
    </table>}
  </div>);
}

const CLASSIFICATOR_FIELDS = [
  { key: "colour_id", table: "wine_colours", label: "Colour" },
  { key: "sugar_type_id", table: "wine_sugar_types", label: "Sugar" },
  { key: "co2_type_id", table: "wine_co2_types", label: "CO2" },
  { key: "spirit_type_id", table: "wine_spirit_types", label: "Spirit" },
  { key: "special_type_id", table: "wine_special_types", label: "Special" },
  { key: "assemblage_type_id", table: "wine_assemblage_types", label: "Assemblage" },
  { key: "origin_control_level_id", table: "wine_origin_control_levels", label: "Origin control" },
  { key: "price_range_id", table: "wine_price_ranges", label: "Price range" },
];

const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp";

function WineImageBox({ wine, onUploaded }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pick = () => { if (!busy) inputRef.current?.click(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg","image/png","image/webp"].includes(file.type)) {
      setError("Use JPEG, PNG, or WebP"); return;
    }
    setBusy(true); setError("");
    try {
      const { upload_url, key } = await req(`/manage/wines/${wine.wine_id}/image-upload-url`, { method:"POST", body: JSON.stringify({ content_type: file.type }) });
      const put = await fetch(upload_url, { method:"PUT", headers:{ "Content-Type": file.type }, body: file });
      if (!put.ok) throw new Error("Upload to storage failed");
      await req(`/manage/wines/${wine.wine_id}/image`, { method:"PUT", body: JSON.stringify({ key }) });
      onUploaded();
    } catch (err) { setError(err.message || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div style={{flexShrink:0,marginLeft:12}}>
      <div
        onClick={pick}
        title={wine.image_url ? "Click to replace photo" : "Click to add a photo"}
        style={{
          width:84,height:112,borderRadius:8,overflow:"hidden",flexShrink:0,cursor:busy?"default":"pointer",
          border: wine.image_url ? "1px solid #252525" : "1px dashed #C4D0A8",
          background: wine.image_url ? `#E8EDE0 url(${wine.image_url}) center/cover no-repeat` : "#E8EDE0",
          display:"flex",alignItems:"center",justifyContent:"center",color:"#A8B89A",position:"relative",
        }}
      >
        {!wine.image_url && (busy ? <Loader size={22}/> : <ImageIcon size={32}/>)}
        {wine.image_url && busy && (
          <div style={{position:"absolute",inset:0,background:"rgba(30,44,20,0.55)",display:"flex",alignItems:"center",justifyContent:"center",color:"#EDF1E4"}}>
            <Loader size={22}/>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept={IMAGE_ACCEPT} onChange={onFile} style={{display:"none"}}/>
      {error && <div style={{fontSize:10,color:"#886070",marginTop:4,maxWidth:84,textAlign:"center"}}>{error}</div>}
    </div>
  );
}

function WineryGalleryCard({ wineryId }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = React.useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await req(`/manage/winery/${wineryId}/images`); setImages(d.images || []); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [wineryId]);

  useEffect(() => { load(); }, [load]);

  const pick = () => { if (!busy) inputRef.current?.click(); };

  const uploadOne = async (file) => {
    const { upload_url, key } = await req(`/manage/winery/${wineryId}/image-upload-url`, { method:"POST", body: JSON.stringify({ content_type: file.type }) });
    const put = await fetch(upload_url, { method:"PUT", headers:{ "Content-Type": file.type }, body: file });
    if (!put.ok) throw new Error("Upload to storage failed");
    await req(`/manage/winery/${wineryId}/images`, { method:"POST", body: JSON.stringify({ key }) });
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const bad = files.filter(f => !["image/jpeg","image/png","image/webp"].includes(f.type));
    if (bad.length) { setError(`${bad.length===files.length ? "Use" : "Skipped " + bad.length + " file(s) — use"} JPEG, PNG, or WebP`); }
    const good = files.filter(f => ["image/jpeg","image/png","image/webp"].includes(f.type));
    if (!good.length) return;
    setBusy(true);
    // Sequential, not parallel — each upload also flips is_title server-side
    // (first photo ever = automatic title), so races here could leave two
    // photos both briefly "first". One at a time keeps that deterministic.
    let failures = 0;
    for (const file of good) {
      try { await uploadOne(file); }
      catch { failures += 1; }
    }
    if (failures) setError(`${failures} of ${good.length} photo(s) failed to upload`);
    await load();
    setBusy(false);
  };

  const setTitle = async (imageId) => {
    setBusy(true); setError("");
    try { await req(`/manage/winery/${wineryId}/images/${imageId}/title`, { method:"PUT" }); await load(); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const remove = async (imageId) => {
    if (!window.confirm("Delete this photo?")) return;
    setBusy(true); setError("");
    try { await req(`/manage/winery/${wineryId}/images/${imageId}`, { method:"DELETE" }); await load(); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{...S.card, marginTop:16}}>
      <div style={{fontSize:10,fontWeight:600,color:"#7A8E68",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:12}}>Photos</div>
      <p style={{fontSize:12,color:"#6A7E58",margin:"0 0 12px"}}>The photo marked "Title" is the one travelers see on their tour itinerary. Click a photo to make it the title image.</p>
      {error && <div style={{fontSize:11,color:"#886070",marginBottom:8}}>{error}</div>}
      <div style={{display:"flex",flexWrap:"wrap",gap:12}}>
        {loading ? <Loader size={18}/> : images.map(img => (
          <div key={img.id} style={{position:"relative",width:110,flexShrink:0}}>
            <div
              onClick={() => !img.is_title && !busy && setTitle(img.id)}
              title={img.is_title ? "Title image" : "Click to make this the title image"}
              style={{
                width:110,height:110,borderRadius:8,overflow:"hidden",cursor: img.is_title||busy ? "default" : "pointer",
                border: img.is_title ? "2px solid #6B2737" : "1px solid #252525",
                background: img.image_url ? `#E8EDE0 url(${img.image_url}) center/cover no-repeat` : "#E8EDE0",
              }}
            />
            {img.is_title && (
              <div style={{position:"absolute",top:4,left:4,background:"#6B2737",color:"#EDF1E4",fontSize:9,fontWeight:600,padding:"2px 6px",borderRadius:4}}>TITLE</div>
            )}
            <button onClick={()=>remove(img.id)} disabled={busy} title="Delete photo" style={{position:"absolute",top:4,right:4,background:"rgba(30,44,20,0.7)",border:"none",borderRadius:5,color:"#EDF1E4",padding:4,cursor:"pointer",display:"flex"}}>
              <Trash2 size={12}/>
            </button>
          </div>
        ))}
        <div
          onClick={pick}
          title="Add photos (you can select more than one)"
          style={{
            width:110,height:110,borderRadius:8,flexShrink:0,cursor:busy?"default":"pointer",
            border:"1px dashed #C4D0A8",background:"#E8EDE0",
            display:"flex",alignItems:"center",justifyContent:"center",color:"#A8B89A",
          }}
        >
          {busy ? <Loader size={20}/> : <ImageIcon size={26}/>}
        </div>
      </div>
      <input ref={inputRef} type="file" accept={IMAGE_ACCEPT} multiple onChange={onFile} style={{display:"none"}}/>
    </div>
  );
}

function WinesTab({ wineryId }) {
  const [wines, setWines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dicts, setDicts] = useState({});
  const [grapes, setGrapes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", vintage_year: "", price: "" });
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [binding, setBinding] = useState(null);
  const [error, setError] = useState("");

  const load = () => { setLoading(true); req(`/manage/winery/${wineryId}/wines`).then(setWines).catch(console.error).finally(()=>setLoading(false)); };
  useEffect(() => { load(); }, [wineryId]);

  useEffect(() => {
    Promise.all(CLASSIFICATOR_FIELDS.map(f => req(`/manage/admin/classificators/${f.table}`).then(rows => [f.table, rows])))
      .then(pairs => setDicts(Object.fromEntries(pairs)))
      .catch(console.error);
    req(`/manage/admin/classificators/grape_varieties`).then(setGrapes).catch(console.error);
  }, []);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setResults(null); };
  const label = (row) => row?.name_translations?.en || row?.name || "—";

  const lookup = async () => {
    setError(""); setSearching(true); setResults(null);
    try {
      const qp = new URLSearchParams();
      CLASSIFICATOR_FIELDS.forEach(f => { if (form[f.key]) qp.set(f.key, form[f.key]); });
      if (form.grape_variety_id) qp.set("grape_variety_id", form.grape_variety_id);
      const rows = await req(`/manage/wines/search?${qp.toString()}`);
      setResults(rows);
    } catch (e) { setError(e.message); }
    finally { setSearching(false); }
  };

  const bindPayload = (src) => {
    const body = { name: src.name };
    if (src.vintage_year) body.vintage_year = src.vintage_year;
    if (src.grape_variety_id) body.grape_variety_id = src.grape_variety_id;
    CLASSIFICATOR_FIELDS.forEach(f => { if (src[f.key]) body[f.key] = src[f.key]; });
    if (form.price) body.price = parseFloat(form.price);
    return body;
  };

  const doBind = async (body, key) => {
    setError(""); setBinding(key);
    try {
      await req(`/manage/winery/${wineryId}/wines`, { method: "POST", body: JSON.stringify(body) });
      setForm({ name: "", vintage_year: "", price: "" });
      setResults(null);
      setShowForm(false);
      load();
    } catch (e) { setError(e.message); }
    finally { setBinding(null); }
  };

  const bindExisting = (row) => doBind(bindPayload(row), row.wine_id);

  const bindNew = () => {
    if (!form.name.trim()) { setError("Name required to bind as a new wine"); return; }
    const src = { name: form.name.trim() };
    if (form.vintage_year) src.vintage_year = parseInt(form.vintage_year);
    if (form.grape_variety_id) src.grape_variety_id = parseInt(form.grape_variety_id);
    CLASSIFICATOR_FIELDS.forEach(f => { if (form[f.key]) src[f.key] = parseInt(form[f.key]); });
    doBind(bindPayload(src), "new");
  };

  const unbind = async (bindingId) => {
    if (!window.confirm("Remove this wine from your winery?")) return;
    try { await req(`/manage/winery/${wineryId}/wines/${bindingId}`, { method: "DELETE" }); load(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <span style={{fontSize:14,fontWeight:600,color:"#1E2C14"}}>{wines.length} Wine{wines.length!==1?"s":""}</span>
        <button onClick={()=>{setShowForm(s=>!s);setResults(null);}} style={S.btn()}>{showForm ? "✕ Cancel" : <><Plus size={13}/>Add wine</>}</button>
      </div>
      {error && <div style={{background:"rgba(139,26,47,0.08)",color:"#8B1A2F",padding:"10px 12px",borderRadius:8,fontSize:12,marginBottom:12}}>{error}</div>}
      {showForm && (
        <div style={{...S.card,marginBottom:16}}>
          <div style={{fontSize:10,fontWeight:600,color:"#7A8E68",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:14}}>New wine</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div style={{gridColumn:"1/-1"}}><label style={S.label}>Name *</label><input style={S.input} value={form.name} onChange={e=>set("name",e.target.value)} placeholder="e.g. Blaufränkisch Reserve"/></div>
            <div><label style={S.label}>Vintage year</label><input style={S.input} type="number" value={form.vintage_year} onChange={e=>set("vintage_year",e.target.value)}/></div>
            <div><label style={S.label}>Price (€)</label><input style={S.input} type="number" step="0.01" value={form.price} onChange={e=>set("price",e.target.value)}/></div>
            {CLASSIFICATOR_FIELDS.map(f => (
              <div key={f.key}>
                <label style={S.label}>{f.label}</label>
                <select style={S.input} value={form[f.key]||""} onChange={e=>set(f.key,e.target.value)}>
                  <option value="">—</option>
                  {(dicts[f.table]||[]).filter(r=>r.id!==1).map(r=><option key={r.id} value={r.id}>{label(r)}</option>)}
                </select>
              </div>
            ))}
            <div style={{gridColumn:"1/-1"}}>
              <label style={S.label}>Grape variety</label>
              <select style={S.input} value={form.grape_variety_id||""} onChange={e=>set("grape_variety_id",e.target.value)}>
                <option value="">—</option>
                {grapes.map(g=><option key={g.id} value={g.id}>{g.name_primary}</option>)}
              </select>
            </div>
          </div>
          <button onClick={lookup} disabled={searching} style={S.btn()}>{searching?<Loader size={13}/>:<Search size={13}/>}Look up wine</button>

          {results !== null && (
            <div style={{marginTop:16,borderTop:"1px solid #1A1A1A",paddingTop:14}}>
              {results.length === 0 ? (
                <div style={{fontSize:12,color:"#6A7E58",marginBottom:12}}>No matching wines found in the catalog yet.</div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
                  {results.map(r => (
                    <div key={r.binding_id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px",background:"#E8EDE0",borderRadius:8}}>
                      <div>
                        <div style={{fontSize:13,color:"#2C3820",fontWeight:500}}>{r.name}{r.vintage_year?` (${r.vintage_year})`:""}</div>
                        <div style={{fontSize:11,color:"#6A7E58"}}>currently carried by {r.winery_name}</div>
                      </div>
                      <button onClick={()=>bindExisting(r)} disabled={binding===r.wine_id} style={{...S.btn("#3D4A1A"),padding:"6px 12px",fontSize:12}}>
                        {binding===r.wine_id?<Loader size={12}/>:"Bind"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={bindNew} disabled={binding==="new"} style={S.btnOut}>
                {binding==="new"?<Loader size={12}/>:<Plus size={12}/>}Bind as new wine
              </button>
            </div>
          )}
        </div>
      )}
      {loading ? <div style={{textAlign:"center",padding:40,color:"#6A7E58"}}>Loading…</div> :
       wines.length===0 ? <div style={{textAlign:"center",padding:40,color:"#6A7E58",fontSize:13}}>No wines yet</div> :
       <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        {wines.map(w => (
          <div key={w.binding_id} style={{position:"relative",background:"#EDF1E4",border:"1px solid #252525",borderRadius:10,padding:"16px 18px 14px 22px",overflow:"hidden"}}>
            <div style={{position:"absolute",left:0,top:0,bottom:0,width:5,background:w.hex_hint||"#6B2737"}}/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
              <div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,color:"#1E2C14",fontWeight:600}}>{w.name}</div>
                {w.vintage_year && <div style={{fontSize:11,color:"#7A8E68",marginTop:2,letterSpacing:"0.03em"}}>Vintage {w.vintage_year}</div>}
                {w.price && <span style={{fontSize:12,fontWeight:600,color:"#6B2737",background:"rgba(107,39,55,0.08)",padding:"3px 10px",borderRadius:100,display:"inline-block",marginTop:6}}>€{w.price}</span>}
              </div>
              <WineImageBox wine={w} onUploaded={load}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",rowGap:6,columnGap:14,borderTop:"1px dashed #C4D0A8",paddingTop:10}}>
              {[
                ["Colour", w.colour_name?.en],
                ["Sugar", w.sugar_type_name?.en],
                ["CO2", w.co2_type_name?.en],
                ["Spirit", w.spirit_type_name?.en],
                ["Special", w.special_type_name?.en],
                ["Assemblage", w.assemblage_type_name?.en],
                ["Origin", w.origin_control_level_name?.en],
                ["Price range", w.price_range_name?.en],
                ["Grape", w.grape_variety_name],
              ].map(([cat,v])=>{
                const shown = (v && v!=="undefined") ? v : "…";
                return (
                  <div key={cat} style={{display:"flex",justifyContent:"space-between",gap:6,fontSize:11}}>
                    <span style={{color:"#7A8E68",textTransform:"uppercase",letterSpacing:"0.05em",fontSize:9,paddingTop:1}}>{cat}</span>
                    <span style={{color:shown==="…"?"#A8B89A":"#2C3820",fontWeight:500,textTransform:"capitalize",textAlign:"right"}}>{shown}</span>
                  </div>
                );
              })}
            </div>
            <div style={{marginTop:12,paddingTop:10,borderTop:"1px solid #E0E5D5",display:"flex",justifyContent:"flex-end"}}>
              <button onClick={()=>unbind(w.binding_id)} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,padding:"5px 10px",borderRadius:6,border:"1px solid #D4A8B0",background:"transparent",color:"#886070",cursor:"pointer"}}>
                <Trash2 size={11}/>Unbind
              </button>
            </div>
          </div>
        ))}
       </div>}
    </div>
  );
}

function ClaimWineryPanel({onCreated}) {
  const [wineries,setWineries]=useState([]);
  const [wineriesError,setWineriesError]=useState(false);
  const [loadingList,setLoadingList]=useState(true);
  const [wineryId,setWineryId]=useState("");
  const [claiming,setClaiming]=useState(false);
  const [error,setError]=useState("");

  useEffect(()=>{
    setLoadingList(true);
    req("/manage/wineries?unclaimed=true").then(list=>{setWineries(Array.isArray(list)?list:[]);setWineriesError(false);})
      .catch(()=>setWineriesError(true)).finally(()=>setLoadingList(false));
  },[]);

  const submit=async()=>{
    if(!wineryId)return;
    setClaiming(true); setError("");
    try{const w=await req(`/manage/winery/${wineryId}/claim`,{method:"POST"});onCreated(w);}
    catch(e){setError(e.message);setClaiming(false);}
  };

  return(<div style={S.card}>
    <div style={{fontSize:10,fontWeight:600,color:"#7A8E68",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:16}}>Find existing winery</div>
    {error && <div style={{background:"rgba(107,39,55,0.1)",border:"1px solid rgba(107,39,55,0.2)",borderRadius:7,padding:"8px 12px",marginBottom:12,fontSize:12,color:"#886070"}}>{error}</div>}
    {wineriesError && <div style={{fontSize:12,color:"#886070",marginBottom:8}}>Could not load wineries list.</div>}
    {!wineriesError && !loadingList && wineries.length===0 && <div style={{fontSize:12,color:"#6A7E58",marginBottom:8}}>No unclaimed wineries available right now.</div>}
    <label style={S.label}>Select winery</label>
    <select style={{...S.input,marginBottom:12}} value={wineryId} onChange={e=>setWineryId(e.target.value)} disabled={loadingList}>
      <option value="">— Select winery —</option>
      {wineries.map(w=><option key={w.id} value={w.id}>{w.name} ({w.road_slug})</option>)}
    </select>
    <p style={{fontSize:11,color:"#6A7E58",marginBottom:0}}>You will be linked as the owner. Only unclaimed wineries are listed.</p>
    <div style={{marginTop:20,display:"flex",justifyContent:"flex-end"}}>
      <button onClick={submit} disabled={claiming||!wineryId} style={S.btn()}>{claiming?<Loader size={13}/>:<Plus size={13}/>}Add to my account</button>
    </div>
  </div>);
}

function RegisterWineryPanel({onCreated}) {
  const [form,setForm]=useState({name:"",description:"",road_slug:"vienna-venice",address:"",lat:"",lng:"",organic:false,price_tier:2,open_time:"10:00",close_time:"17:00",slot_interval_min:90,capacity:8});
  const [saving,setSaving]=useState(false);
  const [error,setError]=useState("");
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const submit=async()=>{
    if(!form.name){setError("Name required");return;}
    setSaving(true); setError("");
    try{const w=await req("/manage/winery",{method:"POST",body:JSON.stringify(form)});onCreated(w);}
    catch(e){setError(e.message);setSaving(false);}
  };
  return(<div style={S.card}>
    <div style={{fontSize:10,fontWeight:600,color:"#7A8E68",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:16}}>Register new winery</div>
    {error && <div style={{background:"rgba(107,39,55,0.1)",border:"1px solid rgba(107,39,55,0.2)",borderRadius:7,padding:"8px 12px",marginBottom:12,fontSize:12,color:"#886070"}}>{error}</div>}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
      <div style={{gridColumn:"1/-1"}}><label style={S.label}>Winery name *</label><input style={S.input} value={form.name} onChange={e=>set("name",e.target.value)} placeholder="e.g. Weingut Mustermann"/></div>
      <div><label style={S.label}>Wine road *</label><select style={S.input} value={form.road_slug} onChange={e=>set("road_slug",e.target.value)}><option value="ar-valley">Ahr Valley</option><option value="vienna-venice">Vienna – Venice</option><option value="malopolska">Małopolska</option></select></div>
      <div><label style={S.label}>Price tier</label><select style={S.input} value={form.price_tier} onChange={e=>set("price_tier",parseInt(e.target.value))}>{PRICE_TIERS.map(t=><option key={t.v} value={t.v}>{t.l}</option>)}</select></div>
      <div style={{gridColumn:"1/-1"}}><label style={S.label}>Description</label><textarea style={{...S.input,minHeight:70,resize:"vertical"}} value={form.description} onChange={e=>set("description",e.target.value)}/></div>
      <div style={{gridColumn:"1/-1"}}><label style={S.label}>Address</label><input style={S.input} value={form.address} onChange={e=>set("address",e.target.value)}/></div>
      <div><label style={S.label}>Latitude</label><input style={S.input} type="number" step="0.0001" value={form.lat} onChange={e=>set("lat",e.target.value)}/></div>
      <div><label style={S.label}>Longitude</label><input style={S.input} type="number" step="0.0001" value={form.lng} onChange={e=>set("lng",e.target.value)}/></div>
      <div style={{display:"flex",alignItems:"center",gap:8,gridColumn:"1/-1"}}><input type="checkbox" id="org2" checked={form.organic} onChange={e=>set("organic",e.target.checked)}/><label htmlFor="org2" style={{fontSize:13,color:"#4A5E38",cursor:"pointer"}}>Certified organic</label></div>
      <div style={{gridColumn:"1/-1",borderTop:"1px solid #1A1A1A",paddingTop:16}}>
        <div style={{fontSize:10,fontWeight:600,color:"#7A8E68",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:12}}>Initial availability</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10}}>
          <div><label style={S.label}>Open</label><input style={S.input} type="time" value={form.open_time} onChange={e=>set("open_time",e.target.value)}/></div>
          <div><label style={S.label}>Close</label><input style={S.input} type="time" value={form.close_time} onChange={e=>set("close_time",e.target.value)}/></div>
          <div><label style={S.label}>Interval (min)</label><input style={S.input} type="number" min={30} step={15} value={form.slot_interval_min} onChange={e=>set("slot_interval_min",parseInt(e.target.value))}/></div>
          <div><label style={S.label}>Capacity</label><input style={S.input} type="number" min={1} value={form.capacity} onChange={e=>set("capacity",parseInt(e.target.value))}/></div>
        </div>
      </div>
    </div>
    <div style={{marginTop:20,display:"flex",justifyContent:"flex-end"}}><button onClick={submit} disabled={saving||!form.name} style={S.btn()}>{saving?<Loader size={13}/>:<Plus size={13}/>}Register winery</button></div>
  </div>);
}

function OnboardTab({onCreated}) {
  const [mode,setMode]=useState("claim"); // "claim" | "create"
  return(<div>
    <div style={{display:"flex",gap:8,marginBottom:16}}>
      <button type="button" onClick={()=>setMode("claim")} style={{flex:1,padding:"8px",background:mode==="claim"?"#E2D8DA":"transparent",border:mode==="claim"?"1px solid #2E1E22":"1px solid #222",borderRadius:7,color:mode==="claim"?"#886070":"#6A7E58",fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Find existing winery</button>
      <button type="button" onClick={()=>setMode("create")} style={{flex:1,padding:"8px",background:mode==="create"?"#E2D8DA":"transparent",border:mode==="create"?"1px solid #2E1E22":"1px solid #222",borderRadius:7,color:mode==="create"?"#886070":"#6A7E58",fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Register new winery</button>
    </div>
    {mode==="claim" ? <ClaimWineryPanel onCreated={onCreated}/> : <RegisterWineryPanel onCreated={onCreated}/>}
  </div>);
}



export default function WineryManagerPage() {
  const navigate=useNavigate();
  const { user, winery: authWinery, logout, loading: authLoading } = useAuth("winery_owner");
  const [searchParams] = useSearchParams();
  const [tab,setTab]=useState(searchParams.get("tab") || "details");
  const [wineries,setWineries]=useState([]);
  const [selectedId,setSelectedId]=useState(null);
  const [winery,setWinery]=useState(null);
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    if(!authLoading && !user) navigate("/manage/login",{state:{from:window.location.pathname+window.location.search}});
  },[user,authLoading,navigate]);

  useEffect(()=>{req("/manage/wineries").then(setWineries).catch(console.error);},[]);

  // Set winery from auth context (user's own winery)
  useEffect(()=>{
    // Deep links from the monthly pre-fill email name the winery explicitly
    // (an owner can manage several); that wins over the default winery.
    if(searchParams.get("winery")) setSelectedId(searchParams.get("winery"));
    else if(authWinery?.id) setSelectedId(authWinery.id);
    else if(!authLoading && user && !authWinery) setSelectedId(null);
  },[authWinery, authLoading, user]);
  useEffect(()=>{
    if(!selectedId)return;
    setLoading(true);
    req(`/manage/winery/${selectedId}`).then(setWinery).catch(console.error).finally(()=>setLoading(false));
  },[selectedId]);

  const TABS=[{id:"details",label:"Details",icon:<Wine size={12}/>},{id:"wines",label:"Wines",icon:<Wine size={12}/>},{id:"availability",label:"Availability",icon:<Calendar size={12}/>},{id:"bookings",label:"Bookings",icon:<BookOpen size={12}/>},{id:"onboard",label:"Add winery",icon:<Plus size={12}/>}];

  return(<div style={S.page}>
    <div style={S.topbar}>
      <button onClick={()=>navigate("/")} style={{background:"none",border:"none",color:"#5A6E48",cursor:"pointer",display:"flex",alignItems:"center",gap:4,fontSize:12}}><ArrowLeft size={13}/>Back</button>
      <span style={{fontFamily:"'Playfair Display',serif",fontSize:16,color:"#1E2C14",flex:1}}>Winery Manager</span>
      {user&&<div style={{display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:11,color:"#6A7E58"}}>{user.email}</span>
        <button onClick={()=>{logout();navigate("/manage/login");}} style={{...S.btnOut,fontSize:11}}>Sign out</button>
      </div>}
      {tab!=="onboard"&&<select value={selectedId} onChange={e=>setSelectedId(e.target.value)} style={{...S.input,width:"auto",fontSize:12,padding:"6px 10px"}}>
        {wineries.map(w=><option key={w.id} value={w.id}>{w.name}</option>)}
      </select>}
    </div>
    <div style={{display:"flex",gap:0,padding:"0 20px",borderBottom:"1px solid #1A1A1A"}}>
      {TABS.map(t=><button key={t.id} onClick={()=>setTab(t.id)} style={S.tab(tab===t.id)}>{t.icon}{t.label}</button>)}
    </div>
    <div style={{padding:20}}>
      {tab!=="onboard"&&loading&&<div style={{textAlign:"center",padding:40,color:"#6A7E58"}}>Loading…</div>}
      {tab==="details"&&!loading&&winery&&<>
        <DetailsTab winery={winery} onSave={setWinery}/>
        <AccountPrivacyPanel onDeleted={()=>{logout();navigate("/manage/login");}}/>
      </>}
      {tab==="wines"&&!loading&&selectedId&&<WinesTab wineryId={selectedId}/>}
      {tab==="availability"&&!loading&&selectedId&&<AvailabilityTab wineryId={selectedId} prefillId={searchParams.get("prefill")} initialMonth={searchParams.get("month")}/>}
      {tab==="bookings"&&!loading&&selectedId&&<BookingsTab wineryId={selectedId}/>}
      {tab==="onboard"&&<OnboardTab onCreated={w=>{setWineries(ws=>[...ws,w]);setSelectedId(w.id);setTab("details");}}/>}
    </div>
  </div>);
}
